import { withIDBFallback, IDB_STORES, IDB_TTL } from "./IDBCacheService";
import { getSeedFromMemory } from "./SeedPrefetchService";
import { withPartNumberSkuBody } from "../helpers/helpers";

type FetchCheckCacheEntry = {
    expires: number;
    data: any;
};

type CheckResult = {
    compatibility?: string;
    custom_compatibility?: string;
    compatibility_custom?: string;
    // Unforced compatibility_custom, captured before the scs_force_normal
    // override below. isCustomRearAvailable() reads this so the custom rear
    // seats add-on (gated separately by window.scs_custom_rear_seats) keeps
    // working even on vehicles/pages where scs_force_normal suppresses the
    // main custom bundle flow.
    compatibility_custom_raw?: string;
    availability?: Record<string, Record<string, boolean>>;
    vehicle_type?: string;
    [key: string]: unknown;
};

/**
 * Build the check seed key used for offline fallback.
 * Keyed by year/make/model/wtype only — the bundled seed only ever stores
 * one generic entry per model, with NO trim/cab granularity. Its Bundle/Set/
 * rear-seat SKUs are whatever the backend returns for a year/make/model-only
 * request — a generic/virtual placeholder — not the trim/cab-specific
 * number. See the trim/cab stripping in seedFallback() below: this key
 * finding a match does NOT mean the result is trim/cab-accurate.
 */
const buildCheckSeedKey = (
    year: string | number,
    make: string,
    model: string,
    wtype: string,
): string => `check_${year}_${make}_${model}_${wtype}`;

/**
 * Trim/cab-specific check seed key — matches the entries the generator writes
 * for show_trim_cab vehicles (see scripts/generate-idb-seed.js --trim-cab).
 * Tried before the generic, trim/cab-blind buildCheckSeedKey above. A hit
 * here is genuinely trim/cab-accurate, so it's returned as-is — no stripping.
 */
const buildCheckSeedKeyTrimCab = (
    year: string | number,
    make: string,
    model: string,
    trim: string,
    cab: string,
    wtype: string,
): string => `check_${year}_${make}_${model}_${trim}_${cab}_${wtype}`;

// "Front Seats" is the only seat key verified stable across every one of a
// model's sku_mappings/rear-seat styles. Every other key (Bundle, Set, Front
// and Universal Rear Seats, Front & Back Bundle, Back Bench, Universal Back
// Bench, Rear Seats, Universal Rear Seats Only, ...) can vary by rear-seat
// style, which is only resolved server-side when trim+cab are supplied.
// Allow-list rather than deny-list: the backend has added new literal-label
// keys to this same shape before (e.g. "Front and Universal Rear Seats" and
// "Universal Rear Seats Only" alongside "Bundle"/"Set"), so enumerating every
// dependent key by name is fragile — anything that isn't "Front Seats" gets
// stripped instead.
const TRIM_CAB_STABLE_SEAT_KEYS = new Set(["Front Seats"]);

/**
 * The bundled offline seed has no trim/cab-specific data — it's a single
 * generic snapshot per year/make/model. When the caller asked for a specific
 * trim/cab, that generic snapshot's Bundle/Set/rear-seat new_sku values are
 * NOT known to be correct for that trim/cab (they can differ per rear-seat
 * style — e.g. a 2025 Ram 1500 Big Horn Crew Cab's Bundle SKU is trim/cab-
 * resolved server-side and won't match the generic snapshot).
 *
 * Only the `new_sku` field is stripped from those seat entries — availability
 * booleans, prekit, and components are left intact. Those drive seat
 * selectability/pricing elsewhere in the app; removing them would make a
 * genuinely available bundle look unavailable and cause the UI to silently
 * fall back to a different seat/color. The reported part number is the only
 * thing that must never show the wrong (generic) value; "no part number"
 * beats "the wrong part number", but "wrong seat auto-selected" is worse
 * than either. "Front Seats" is left untouched since its SKU doesn't vary
 * this way.
 */
const stripTrimCabDependentKeys = (
    result: CheckResult,
    hadTrimOrCab: boolean,
): CheckResult => {
    if (!hadTrimOrCab || !result.availability || typeof result.availability !== "object") {
        return result;
    }
    const filtered: Record<string, any> = {};
    for (const [seat, val] of Object.entries(result.availability)) {
        if (TRIM_CAB_STABLE_SEAT_KEYS.has(seat) || !val || typeof val !== "object") {
            filtered[seat] = val;
        } else {
            const { new_sku, ...rest } = val as Record<string, any>;
            filtered[seat] = rest;
        }
    }
    return { ...result, availability: filtered };
};

/**
 * Synthesize a check response from a seeded `vehicle_availability` entry.
 *
 * `vehicle_availability` is the configurator data (used by biz2 + vehicle_configurator).
 * Its `data.configurator[].sku_mappings[].availability` matrices are merged
 * into a single seat→color availability map — that's the same shape `check`
 * normally returns under `data.availability`, so we can stand in for it
 * when /api/v1/check is unreachable.
 */
const deriveCheckFromVehicleAvailability = (
    year: string | number,
    make: string,
    model: string,
): CheckResult | null => {
    const vaKey = `vehicle_availability_${year}_${make}_${model}`;
    const va = getSeedFromMemory<any>(IDB_STORES.VEHICLE_AVAILABILITY, vaKey);
    if (!va) return null;
    // Seed stores the inner data directly (no `data` wrapper);
    // live API shape has { data: { vehicle, availability, configurator } }.
    const vaData = va.data ?? va;
    if (!vaData) return null;

    const compatibility = vaData.vehicle?.compatibility ?? "Y";
    const merged: Record<string, Record<string, boolean>> = {};
    const configurator = Array.isArray(vaData.configurator) ? vaData.configurator : [];
    for (const cfg of configurator) {
        const mappings = Array.isArray(cfg?.sku_mappings) ? cfg.sku_mappings : [];
        for (const mapping of mappings) {
            const avail = mapping?.availability || {};
            for (const [seat, byColor] of Object.entries(avail)) {
                if (!byColor || typeof byColor !== "object") continue;
                if (!merged[seat]) merged[seat] = {};
                for (const [color, val] of Object.entries(byColor as Record<string, unknown>)) {
                    if (typeof val === "boolean") {
                        merged[seat][color] = merged[seat][color] || val;
                    }
                }
            }
        }
    }

    // Fall back to the top-level `availability` field on the VA response when
    // the configurator block doesn't supply it (vehicles without sku_mappings).
    if (Object.keys(merged).length === 0 && vaData.availability && typeof vaData.availability === "object") {
        for (const [seat, byColor] of Object.entries(vaData.availability)) {
            if (!byColor || typeof byColor !== "object") continue;
            merged[seat] = {};
            for (const [color, val] of Object.entries(byColor as Record<string, unknown>)) {
                if (typeof val === "boolean") merged[seat][color] = val;
            }
        }
    }

    return {
        compatibility,
        custom_compatibility: "N",
        compatibility_custom: "N",
        availability: merged,
        vehicle_type: vaData.vehicle?.vehicle_type ?? vaData.vehicle_type,
    };
};

const FETCH_CHECK_CACHE_TTL = 60 * 1000; // 60 seconds
const fetchCheckCache = new Map<string, FetchCheckCacheEntry>();

const buildFetchCheckCacheKey = (params: Record<string, any>) => {
    return JSON.stringify(
        Object.keys(params)
            .sort()
            .reduce((acc, key) => {
                acc[key] = params[key];
                return acc;
            }, {} as Record<string, any>)
    );
};

const fetchCheck = async (
    year,
    make,
    model,
    trim,
    color,
    seat,
    cabSize = "",
    step = ""
) => {
    if (!year) return [];
    if (!make) return [];
    if (!model) return [];

    let wtype = "normal";
    if (window?.scs_force_normal) {
        wtype = "normal";
    } else if (window?.is_real_truck) {
        wtype = "realtruck";
    } else if (window?.scs_force_custom) {
        wtype = "custom";
    } else if (window?.scs_custom_endpoint) {
        wtype = "realtruck";
    }

    const params = {
        year: year,
        make: make,
        model: model,
        colorCode: color || "Black",
        wtype: wtype,
    };
    if (trim && trim !== "") {
        params["trim"] = trim;
    }
    if (cabSize && cabSize !== "") {
        params["cab"] = cabSize;
    }

    if (seat && !window.scs_new_design_2026) {
        params["type"] = seat;
    }
    // if (step === "change_seat") {
    //     params["type"] = seat;
    // }
    if (window.scs_page_seo === true) {
        params["page_seo"] = "1";
    }

    const cacheKey = buildFetchCheckCacheKey(params);
    const cached = fetchCheckCache.get(cacheKey);

    if (cached && cached.expires > Date.now()) {
        return cached.data;
    }

    // Inner function: network request + response processing.
    // This is what gets stored in (and served from) IDB on network failure.
    const fetchAndProcess = async () => {
        const urlMakes = "https://scsbiz3.wearevisum.com/api/v1/check";
        const response = await fetch(urlMakes, {
            method: "POST",
            mode: "cors",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(withPartNumberSkuBody(params)),
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch check: ${response.statusText}`);
        }

        const resp = await response.json();
        resp.data.compatibility_custom = resp.data.custom_compatibility;

        // Hard override: for configurator flow, force HF for 2002-2008 RAM 1500/2500/3500/4500/5500
        const isConfiguratorOverride = window?.scs_vehicle_configurator === true &&
            typeof year !== 'undefined' &&
            typeof make !== 'undefined' &&
            typeof model !== 'undefined' &&
            Number(year) >= 2002 && Number(year) <= 2008 &&
            String(make).toLowerCase() === 'ram' &&
            /(^|\D)(1500|2500|3500|4500|5500)(\D|$)/i.test(String(model));

        if (isConfiguratorOverride) {
            resp.data.compatibility = 'HF';
            // also set custom compatibility to match if present
            resp.data.custom_compatibility = resp.data.custom_compatibility || 'HF';
        }

        // console.log('check resp', resp)

        // Preserve HF when configurator override applied; otherwise normalize HF -> F as before
        if (!isConfiguratorOverride && resp.data.compatibility === "HF") {
            resp.data.compatibility = "F";
        }
        if (
            resp.data.compatibility === "N" ||
            resp.data.compatibility === "DNE" ||
            resp.data.compatibility === "NAON"
        ) {
            resp.data.compatibility = "N";
        }

        // Capture before scs_force_normal below can zero it out — the custom
        // rear seats add-on is gated independently by window.scs_custom_rear_seats
        // and must keep working even when scs_force_normal suppresses the main
        // custom bundle flow for this vehicle/page.
        resp.data.compatibility_custom_raw = resp.data.compatibility_custom;

        if (window.scs_force_normal === true) {
            resp.data.custom_compatibility = "N";
            resp.data.compatibility_custom = "N";
        } else {
            if (window.scs_force_custom === true) {
                resp.data.compatibility = "N";
            }
        }

        resp.data.custom_compatibility = resp.data.compatibility_custom;

        // if (window.scs_new_design_2026) {
        //     const keyOrder = ["Bundle", "Front & Back Bundle", "Front Seats", "Back Bench", "Universal Back Bench"];
        //     // need to sort resp.data.availability by key order from keyOrder
        //
        //     const availability = resp.data.availability;
        //
        //     resp.data.availability = {
        //         ...Object.fromEntries(
        //             keyOrder
        //                 .filter(key => key in availability)
        //                 .map(key => [key, availability[key]])
        //         ),
        //         ...Object.fromEntries(
        //             Object.entries(availability).filter(
        //                 ([key]) => !keyOrder.includes(key)
        //             )
        //         ),
        //     };
        //
        //
        //     console.log('check resp 2:', resp)
        // }

        return resp.data;
    };

    // Seed fallbacks, tried in order:
    //   1. Trim/cab-specific seed entry (generator's --trim-cab pass) — genuinely
    //      accurate for this exact vehicle, returned as-is.
    //   2. Generic year/make/model seed entry — no trim/cab granularity, so its
    //      Bundle/Set/rear-seat SKUs aren't known to be correct for a specific
    //      trim/cab request; strip those (see stripTrimCabDependentKeys).
    //   3. Derivation from the bundled vehicle_availability data — same caveat as 2.
    const hadTrimOrCab = !!(params["trim"] || params["cab"]);
    const seedFallback = (): CheckResult | null => {
        if (hadTrimOrCab) {
            const trimCabSeedKey = buildCheckSeedKeyTrimCab(year, make, model, params["trim"] ?? "", params["cab"] ?? "", wtype);
            const trimCabSeeded = getSeedFromMemory<CheckResult>(IDB_STORES.CHECK, trimCabSeedKey);
            if (trimCabSeeded && typeof trimCabSeeded === "object") {
                return trimCabSeeded;
            }
        }
        const seedKey = buildCheckSeedKey(year, make, model, wtype);
        const seeded = getSeedFromMemory<CheckResult>(IDB_STORES.CHECK, seedKey);
        if (seeded && typeof seeded === "object") {
            return stripTrimCabDependentKeys(seeded, hadTrimOrCab);
        }
        const derived = deriveCheckFromVehicleAvailability(year, make, model);
        if (derived) {
            return stripTrimCabDependentKeys(derived, hadTrimOrCab);
        }
        return null;
    };

    let result: CheckResult | undefined;
    try {
        result = await withIDBFallback(
            IDB_STORES.CHECK,
            cacheKey,
            IDB_TTL.CHECK,
            fetchAndProcess
        ) as CheckResult;
    } catch (err) {
        console.warn("[CheckService] network+IDB failed, attempting seed fallback:", err);
        const fallback = seedFallback();
        if (fallback) {
            result = fallback;
        } else {
            throw err;
        }
    }

    // withIDBFallback may resolve with a malformed/empty value if the cached
    // entry was written by a prior failed run — guard with the same fallback.
    if (!result || typeof result !== "object" || !result.compatibility) {
        const fallback = seedFallback();
        if (fallback) result = fallback;
    }

    if (result && !(result as any).error) {
        fetchCheckCache.set(cacheKey, {
            expires: Date.now() + FETCH_CHECK_CACHE_TTL,
            data: result,
        });
    }

    return result;
};

export default fetchCheck;
